<template>
	<div class="fx-game">
		<header v-if="ready" class="fx-game__topbar">
			<div class="fx-game__topbar-left">
				<button class="fx-game__ctrl fx-game__ctrl--back" title="返回博客首页" @click="$router.push('/home')">← 返回博客</button>
				<div class="fx-game__brand">
					<span class="fx-game__logo">
						<svg class="fx-game__logo-mark" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
							<rect x="4" y="9" width="4" height="8" rx="1" fill="#2ebd85" />
							<rect x="5.5" y="6" width="1" height="14" rx="0.5" fill="#2ebd85" opacity="0.75" />
							<rect x="15" y="6" width="4" height="9" rx="1" fill="#f5564a" />
							<rect x="16.5" y="3" width="1" height="14" rx="0.5" fill="#f5564a" opacity="0.75" />
						</svg>
						FX 外汇模拟
					</span>
					<span class="fx-game__sub">游戏 · 非真实交易</span>
				</div>
			</div>
			<StatusBar
				:equity="equity"
				:balance="account.balance"
				:position-count="account.positions.length"
				:debt="account.debt"
				:interest-paid="account.interestPaid"
				:initial-balance="config.initialBalance * 100"
			/>
		</header>

		<main v-if="ready" class="fx-game__main" :style="{ height: mainHeight + 'px' }">
			<section class="fx-game__chart">
				<PriceChart
					:klines="klines"
					:latest-price="latestPrice"
					:open-price-ref="openPriceRef"
					:symbol-name="selectedSymbolName"
				/>
			</section>
			<div
				class="fx-game__grip fx-game__grip--v"
				title="拖动调整图表宽度（右侧面板宽度）"
				@mousedown="startResize('side', $event)"
			></div>
			<aside class="fx-game__side" :style="{ width: sideWidth + 'px' }">
				<TradePanel
					:symbols="config.symbols"
					:selected-code="selectedCode"
					:leverages="config.leverages"
					:leverage="leverage"
					:disabled="gameOver || !ready || paused"
					:balance-cents="account.balance"
					:fee-cents="Math.round(config.openFee * 100)"
					:compact="isSideCompact"
					@select-symbol="selectSymbol"
					@select-leverage="leverage = $event"
					@open="handleOpen"
					@update-margin="marginCents = $event"
				/>
				<BorrowPanel
					:debt="account.debt"
					:interest-paid="account.interestPaid"
					:rate="currentRate"
					:tiers="config.borrowTiers"
					:active-tier-limit="activeTierLimit"
					:max-cents="config.borrowMax * 100"
					:day-seconds="config.borrowDaySeconds"
					:disabled="gameOver || !ready || paused"
					:compact="isSideCompact"
					@borrow="handleBorrow"
					@repay="handleRepay"
				/>
			</aside>
		</main>

		<div
			v-if="ready"
			class="fx-game__grip fx-game__grip--h"
			title="拖动调整图表高度（下侧区域高度）"
			@mousedown="startResize('main', $event)"
		></div>

		<section v-if="ready" class="fx-game__bottom">
			<div class="fx-game__positions">
				<PositionsPanel
					:positions="positionViewModels"
					:disabled="gameOver || !ready || paused"
					@close="handleClose"
				/>
			</div>
			<div class="fx-game__log">
				<TradeLogPanel :log="log" />
			</div>
			<div class="fx-game__controls">
				<button class="fx-game__ctrl fx-game__ctrl--reset" @click="handleReset">回到起点</button>
				<button class="fx-game__ctrl" :class="{ 'is-paused': paused }" :disabled="!ready" @click="togglePause">
					{{ paused ? '继续' : '暂停' }}
				</button>
			</div>
		</section>

		<footer v-if="ready" class="fx-game__footer">
			本页面为 FX 外汇模拟游戏，仅供娱乐，不提供真实交易，不构成任何投资建议。
		</footer>

		<div v-if="errorMsg" class="fx-game__toast"><span class="fx-game__toast-icon">!</span>{{ errorMsg }}</div>

		<div v-if="gameOver" class="fx-game__overlay">
			<div class="fx-game__overlay-card">
				<div class="fx-game__overlay-num">$0.00</div>
				<div class="fx-game__overlay-title">你破产了</div>
				<div class="fx-game__overlay-sub">权益归零，点击回到起点重新开始</div>
				<button class="fx-game__overlay-btn" @click="handleReset">回到起点</button>
			</div>
		</div>

		<div v-if="!ready" class="fx-game__loading">
			<div class="fx-game__loading-ring"></div>
			<div class="fx-game__loading-text">行情加载中...</div>
		</div>
	</div>
</template>

<script>
import StatusBar from './components/StatusBar'
import PriceChart from './components/PriceChart'
import TradePanel from './components/TradePanel'
import BorrowPanel from './components/BorrowPanel'
import PositionsPanel from './components/PositionsPanel'
import TradeLogPanel from './components/TradeLogPanel'
import { getFxConfig, getMarketSeed } from '@/api/fx'
import { currentTickIndex, getDayKey, getKlines, getLatestPrice, resetSeriesCache, setClockOffset } from '@/util/fx/market'
import {
	accrueInterest,
	borrow,
	closePosition,
	computeUnrealizedPnl,
	createAccount,
	getEquity,
	getRateForDebt,
	liquidatePositions,
	openPosition,
	repay,
	resetAccount
} from '@/util/fx/account'

const STORAGE_KEY = 'fx:game:v1'
const LAYOUT_KEY = 'fx:game:layout:v2'
const MAX_OFFLINE_DAYS = 90

/**
 * 主区域（图表 + 右侧面板）最小高度（px）
 */
const MAIN_MIN = 220

/**
 * 下侧区域（持仓/日志/按钮）最小高度（px）
 */
const BOTTOM_MIN = 150

/**
 * 右侧面板最小宽度（px）
 */
const SIDE_MIN = 320

/**
 * 右侧面板最大宽度（px）
 */
const SIDE_MAX = 504

/**
 * 右侧面板进入紧凑布局的宽度阈值（px）
 */
const SIDE_COMPACT_WIDTH = 340

/**
 * 顶栏 + 底栏 + 拖拽条 + 内边距预留高度（px）
 *
 * <p>取偏大的值以覆盖顶栏状态栏换行的情况，避免主区域挤压下侧区域。</p>
 */
const CHROME_HEIGHT = 130

export default {
	name: 'FxGame',
	components: {
		StatusBar,
		PriceChart,
		TradePanel,
		BorrowPanel,
		PositionsPanel,
		TradeLogPanel
	},
	data() {
		return {
			ready: false,
			config: null,
			seedData: null,
			account: null,
			log: [],
			prices: {},
			klines: [],
			latestPrice: 0,
			selectedCode: 'EUR/USD',
			leverage: 25,
			marginCents: 100000,
			paused: false,
			nextDayAt: 0,
			gameOver: false,
			errorMsg: '',
			refreshingSeed: false,
			timer: null,
			mainHeight: 380,
			sideWidth: 340,
			viewportWidth: 0,
			viewportHeight: 0,
			dragging: '',
			dragStart: null
		}
	},
	computed: {
		/**
		 * 主区域最大高度（px）：保证下侧区域不低于最小高度
		 */
		mainMaxHeight() {
			const height = this.viewportHeight || window.innerHeight
			return Math.max(MAIN_MIN, height - CHROME_HEIGHT - BOTTOM_MIN)
		},
		/**
		 * 右侧面板最大宽度（px）：不超过视口一半
		 */
		sideMaxWidth() {
			const width = this.viewportWidth || window.innerWidth
			return Math.max(SIDE_MIN, Math.min(SIDE_MAX, Math.floor(width * 0.5)))
		},
		/**
		 * 右侧面板是否使用紧凑布局
		 */
		isSideCompact() {
			return this.sideWidth < SIDE_COMPACT_WIDTH
		},

		/**
		 * 当前选中品种配置
		 */
		selectedSymbol() {
			if (!this.config) {
				return null
			}
			return this.config.symbols.find(symbol => symbol.code === this.selectedCode) || null
		},
		/**
		 * 选中品种名称
		 */
		selectedSymbolName() {
			return this.selectedSymbol ? this.selectedSymbol.name + ' ' + this.selectedSymbol.code : this.selectedCode
		},
		/**
		 * 当前持仓的权益（分）
		 */
		equity() {
			if (!this.account) {
				return 0
			}
			return getEquity(this.account, this.priceMap)
		},
		/**
		 * 品种价格 Map
		 */
		priceMap() {
			return new Map(Object.entries(this.prices))
		},
		/**
		 * 持仓视图模型
		 */
		positionViewModels() {
			if (!this.account) {
				return []
			}
			return this.account.positions.map(position => {
				const price = this.prices[position.symbol]
				const pnlCents = computeUnrealizedPnl(position, price)
				return {
					...position,
					currentPrice: price,
					pnlCents,
					pnlClass: pnlCents >= 0 ? 'is-up' : 'is-down'
				}
			})
		},
		/**
		 * 选中品种的持仓开仓价参考线
		 */
		openPriceRef() {
			if (!this.account) {
				return null
			}
			const position = this.account.positions.find(item => item.symbol === this.selectedCode)
			return position ? position.openPrice : null
		},
		/**
		 * 当前负债对应的日利率
		 */
		currentRate() {
			if (!this.config || !this.account) {
				return 0
			}
			return getRateForDebt(this.account.debt, this.config.borrowTiers)
		},
		/**
		 * 当前生效的利率档位上限
		 */
		activeTierLimit() {
			if (!this.config || !this.account) {
				return null
			}
			const debtDollars = this.account.debt / 100
			let limit = null
			for (const tier of this.config.borrowTiers) {
				if (debtDollars <= tier.limit) {
					limit = tier.limit
					break
				}
			}
			return limit
		}
	},
	mounted() {
		this.initLayout()
		window.addEventListener('resize', this.handleViewportResize)
		this.init()
	},
	beforeDestroy() {
		this.clearTimer()
		this.stopResize()
		window.removeEventListener('resize', this.handleViewportResize)
	},
	methods: {
		/**
		 * 初始化可拖拽布局尺寸
		 *
		 * <p>优先恢复上次保存的布局，并按当前视口限制在最小/最大值之间，
		 * 保证任意窗口尺寸下不出现裁切。</p>
		 */
		initLayout() {
			this.updateViewport()
			let saved = null
			try {
				const raw = localStorage.getItem(LAYOUT_KEY)
				if (raw) {
					saved = JSON.parse(raw)
				}
			} catch (error) {
				// 布局存档损坏则使用默认值
			}
			const defaultMain = this.clamp(Math.round(this.viewportHeight * 0.44), 378, 414)
			const defaultSide = Math.round(this.viewportWidth * 0.25)
			const savedMain = saved && typeof saved.mainHeight === 'number' ? saved.mainHeight : defaultMain
			const savedSide = saved && typeof saved.sideWidth === 'number' ? saved.sideWidth : defaultSide
			this.mainHeight = this.clamp(savedMain, MAIN_MIN, this.mainMaxHeight)
			this.sideWidth = this.clamp(savedSide, SIDE_MIN, this.sideMaxWidth)
		},
		/**
		 * 记录当前视口尺寸
		 */
		updateViewport() {
			this.viewportWidth = window.innerWidth
			this.viewportHeight = window.innerHeight
		},
		/**
		 * 窗口尺寸变化时重新收敛布局
		 */
		handleViewportResize() {
			this.updateViewport()
			this.mainHeight = this.clamp(this.mainHeight, MAIN_MIN, this.mainMaxHeight)
			this.sideWidth = this.clamp(this.sideWidth, SIDE_MIN, this.sideMaxWidth)
		},
		/**
		 * 开始拖拽调整布局
		 *
		 * @param {string} kind side-调整右侧面板宽度，main-调整主区域高度
		 * @param {MouseEvent} event 鼠标按下事件
		 */
		startResize(kind, event) {
			if (!event || event.button !== 0) {
				return
			}
			event.preventDefault()
			this.dragging = kind
			this.dragStart = {
				x: event.clientX,
				y: event.clientY,
				mainHeight: this.mainHeight,
				sideWidth: this.sideWidth
			}
			document.body.style.userSelect = 'none'
			document.body.style.cursor = kind === 'side' ? 'col-resize' : 'row-resize'
			window.addEventListener('mousemove', this.handleResizeMove)
			window.addEventListener('mouseup', this.stopResize)
			window.addEventListener('blur', this.stopResize)
		},
		/**
		 * 拖拽过程中实时更新布局尺寸
		 *
		 * @param {MouseEvent} event 鼠标移动事件
		 */
		handleResizeMove(event) {
			if (!this.dragging || !this.dragStart) {
				return
			}
			// 鼠标在窗口外松开时收不到 mouseup，用按键状态兜底结束拖拽
			if (event.buttons === 0) {
				this.stopResize()
				return
			}
			if (this.dragging === 'side') {
				const delta = this.dragStart.x - event.clientX
				this.sideWidth = this.clamp(Math.round(this.dragStart.sideWidth + delta), SIDE_MIN, this.sideMaxWidth)
				return
			}
			const delta = event.clientY - this.dragStart.y
			this.mainHeight = this.clamp(Math.round(this.dragStart.mainHeight + delta), MAIN_MIN, this.mainMaxHeight)
		},
		/**
		 * 结束拖拽并保存布局
		 */
		stopResize() {
			if (!this.dragging) {
				return
			}
			this.dragging = ''
			this.dragStart = null
			document.body.style.userSelect = ''
			document.body.style.cursor = ''
			window.removeEventListener('mousemove', this.handleResizeMove)
			window.removeEventListener('mouseup', this.stopResize)
			window.removeEventListener('blur', this.stopResize)
			this.saveLayout()
		},
		/**
		 * 保存布局尺寸到本地
		 */
		saveLayout() {
			try {
				localStorage.setItem(LAYOUT_KEY, JSON.stringify({
					mainHeight: this.mainHeight,
					sideWidth: this.sideWidth
				}))
			} catch (error) {
				// 布局保存失败忽略
			}
		},
		/**
		 * 将数值限制在指定区间内
		 *
		 * @param {number} value 原始值
		 * @param {number} min 最小值
		 * @param {number} max 最大值
		 * @returns {number} 限制后的值
		 */
		clamp(value, min, max) {
			return Math.min(Math.max(value, min), max)
		},
		/**
		 * 初始化游戏
		 */
		async init() {
			try {
				const configResult = await getFxConfig()
				if (configResult && configResult.data) {
					this.config = configResult.data
					this.cacheConfig()
				} else {
					this.config = this.loadCachedConfig()
				}
				const seedResult = await getMarketSeed()
				if (seedResult && seedResult.data) {
					this.applySeed(seedResult.data)
				}
				if (!this.config || !this.seedData) {
					throw new Error('配置或种子加载失败')
				}
				// 组件在异步加载期间被销毁则不再启动定时器
				if (this._isDestroyed) {
					return
				}
				this.restoreAccount()
				this.refreshMarket()
				this.startTimer()
				this.ready = true
			} catch (error) {
				this.config = this.config || this.loadCachedConfig()
				if (!this.config) {
					this.errorMsg = '游戏配置加载失败，请刷新重试'
					return
				}
				this.errorMsg = '行情加载失败，请刷新重试'
			}
		},
		/**
		 * 启动行情循环
		 *
		 * <p>使用自调度的 setTimeout 而非 setInterval：单次 tick 异常时也能继续下一轮，
		 * 且异步 tick 不会重叠堆积，避免长时间运行后行情停止更新。</p>
		 */
		startTimer() {
			this.clearTimer()
			this.scheduleTick()
		},
		/**
		 * 排程下一次行情 tick
		 */
		scheduleTick() {
			this.clearTimer()
			this.timer = setTimeout(async () => {
				try {
					await this.tick()
				} catch (error) {
					console.error('行情更新失败:', error)
				} finally {
					if (!this._isDestroyed) {
						this.scheduleTick()
					}
				}
			}, this.config.tickIntervalMs)
		},
		/**
		 * 清除行情循环
		 */
		clearTimer() {
			if (this.timer) {
				clearInterval(this.timer)
				this.timer = null
			}
		},
		/**
		 * 暂停/继续切换
		 *
		 * <p>恢复时将下一个计息时间重置为当前时刻，避免补扣暂停期间的利息。</p>
		 */
		togglePause() {
			this.paused = !this.paused
			if (this.paused) {
				this.saveState()
			} else {
				this.nextDayAt = Date.now() + this.config.borrowDaySeconds * 1000
				this.saveState()
			}
		},
		/**
		 * 行情 tick
		 */
		async tick() {
			if (this.paused || this.gameOver || !this.ready) {
				return
			}
			await this.ensureSeedCurrent()
			this.refreshMarket()
			this.handleLiquidation()
			this.handleInterest()
			this.checkGameOver()
			this.saveState()
		},
		/**
		 * 确保行情种子为当日种子（跨零点后自动刷新）
		 *
		 * <p>跨日后行情序列会从头开盘，需重新拉取当日种子，避免不同页面使用旧种子。</p>
		 */
		async ensureSeedCurrent() {
			if (this.seedData && this.seedData.date === getDayKey()) {
				return
			}
			if (this.refreshingSeed) {
				return
			}
			this.refreshingSeed = true
			try {
				const result = await getMarketSeed()
				if (result && result.data) {
					this.applySeed(result.data)
					this.pushLog('system', '新交易日开始，行情已更新', null)
				}
			} catch (error) {
				// 种子刷新失败时沿用旧种子，下一 tick 重试
			} finally {
				this.refreshingSeed = false
			}
		},
		/**
		 * 应用行情种子，并用服务器时间校正本地时钟
		 *
		 * @param {object} seed 行情种子
		 */
		applySeed(seed) {
			const previousDate = this.seedData ? this.seedData.date : ''
			this.seedData = seed
			if (previousDate !== seed.date) {
				resetSeriesCache()
			}
			if (seed && typeof seed.serverTime === 'number') {
				setClockOffset(seed.serverTime - Date.now())
			}
		},
		/**
		 * 刷新行情价格与 K 线
		 */
		refreshMarket() {
			// 跨日后旧种子尚未刷新完成时不生成行情，等待下一次 tick
			if (!this.seedData || this.seedData.date !== getDayKey()) {
				return
			}
			const tickIndex = currentTickIndex(this.config.tickIntervalMs)
			const seedMap = this.seedData.symbolSeeds
			const prices = {}
			for (const symbol of this.config.symbols) {
				prices[symbol.code] = getLatestPrice(symbol, seedMap[symbol.code] >>> 0, tickIndex)
			}
			this.prices = prices
			if (this.selectedSymbol) {
				this.klines = getKlines(
					this.selectedSymbol,
					seedMap[this.selectedSymbol.code] >>> 0,
					tickIndex,
					this.config.ticksPerBar,
					this.config.klineBars
				)
				this.latestPrice = prices[this.selectedSymbol.code]
			}
		},
		/**
		 * 检查并处理爆仓
		 */
		handleLiquidation() {
			const result = liquidatePositions(this.account, this.priceMap)
			if (result.liquidated.length > 0) {
				this.account = result.account
				for (const position of result.liquidated) {
					this.pushLog('liquidate', `${position.symbol} ${position.direction === 'long' ? '多' : '空'}单爆仓`, -position.marginCents)
				}
			}
		},
		/**
		 * 处理利息结算
		 */
		handleInterest() {
			if (!this.nextDayAt) {
				this.nextDayAt = Date.now() + this.config.borrowDaySeconds * 1000
			}
			let days = 0
			while (Date.now() >= this.nextDayAt && days < MAX_OFFLINE_DAYS) {
				this.accrueOneDay()
				this.nextDayAt += this.config.borrowDaySeconds * 1000
				days++
			}
			// 超出最大补算天数后丢弃剩余时间，避免逐 tick 无限补扣
			if (Date.now() >= this.nextDayAt) {
				this.nextDayAt = Date.now() + this.config.borrowDaySeconds * 1000
			}
		},
		/**
		 * 结算一日利息
		 */
		accrueOneDay() {
			if (this.account.debt <= 0) {
				return
			}
			const before = this.account.interestPaid
			this.account = accrueInterest(this.account, this.config.borrowTiers)
			const interest = this.account.interestPaid - before
			this.pushLog('interest', '周转金利息结算', -interest)
		},
		/**
		 * 检查是否破产
		 */
		checkGameOver() {
			if (this.equity <= 0) {
				this.gameOver = true
				this.paused = true
				this.pushLog('gameover', '账户权益归零，游戏结束', null)
			}
		},
		/**
		 * 切换品种
		 *
		 * @param {string} code 品种代码
		 */
		selectSymbol(code) {
			this.selectedCode = code
			this.refreshMarket()
		},
		/**
		 * 开仓
		 *
		 * @param {string} direction long/short
		 */
		handleOpen(direction) {
			const price = this.prices[this.selectedCode]
			if (!price) {
				return
			}
			try {
				this.account = openPosition(this.account, {
					symbol: this.selectedCode,
					direction,
					marginCents: this.marginCents,
					leverage: this.leverage,
					openPrice: price,
					feeCents: Math.round(this.config.openFee * 100)
				})
				this.pushLog('open', `${this.selectedCode} 开${direction === 'long' ? '多' : '空'} ${this.leverage}倍`, -this.marginCents)
				this.saveState()
			} catch (error) {
				this.showError(error.message)
			}
		},
		/**
		 * 平仓
		 *
		 * @param {number} positionId 持仓 ID
		 */
		handleClose(positionId) {
			const position = this.account.positions.find(item => item.id === positionId)
			if (!position) {
				return
			}
			const price = this.prices[position.symbol]
			if (price == null) {
				return
			}
			this.account = closePosition(this.account, positionId, price)
			const pnl = computeUnrealizedPnl(position, price)
			this.pushLog('close', `${position.symbol} 平仓`, pnl)
			this.saveState()
		},
		/**
		 * 借钱
		 *
		 * @param {number} amountCents 借入金额（分）
		 */
		handleBorrow(amountCents) {
			try {
				this.account = borrow(this.account, amountCents, Math.round(this.config.borrowMax * 100))
				this.pushLog('borrow', '周转小金库借入', amountCents)
				this.saveState()
			} catch (error) {
				this.showError(error.message)
			}
		},
		/**
		 * 还款
		 *
		 * @param {number} amountCents 还款金额（分）
		 */
		handleRepay(amountCents) {
			try {
				const before = this.account.debt
				this.account = repay(this.account, amountCents)
				const actual = before - this.account.debt
				if (actual > 0) {
					this.pushLog('repay', '周转小金库还款', -actual)
					this.saveState()
				}
			} catch (error) {
				this.showError(error.message)
			}
		},
		/**
		 * 回到起点：重置账户
		 */
		handleReset() {
			this.account = resetAccount(Math.round(this.config.initialBalance * 100))
			this.log = []
			this.gameOver = false
			this.paused = false
			this.nextDayAt = Date.now() + this.config.borrowDaySeconds * 1000
			this.pushLog('system', '新游戏开始', null)
			this.saveState()
		},
		/**
		 * 追加结算日志（保留最近 100 条）
		 *
		 * @param {string} type 日志类型
		 * @param {string} text 日志文本
		 * @param {number|null} amount 金额（分）
		 */
		pushLog(type, text, amount) {
			this.log.unshift({
				type,
				text,
				amount,
				time: this.formatTime(new Date())
			})
			if (this.log.length > 100) {
				this.log.length = 100
			}
		},
		/**
		 * 显示错误提示
		 *
		 * @param {string} message 错误信息
		 */
		showError(message) {
			this.errorMsg = message
			setTimeout(() => {
				if (this.errorMsg === message) {
					this.errorMsg = ''
				}
			}, 3000)
		},
		/**
		 * 格式化时间 HH:MM:SS
		 *
		 * @param {Date} date 日期
		 * @returns {string} 格式化时间
		 */
		formatTime(date) {
			const pad = value => String(value).padStart(2, '0')
			return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
		},
		/**
		 * 恢复存档
		 */
		restoreAccount() {
			const initialCents = Math.round(this.config.initialBalance * 100)
			let saved = null
			try {
				const raw = localStorage.getItem(STORAGE_KEY)
				if (raw) {
					saved = JSON.parse(raw)
				}
			} catch (error) {
				// 存档损坏则忽略
			}
			const savedAccount = saved ? saved.account : null
			const isValidAccount = savedAccount &&
				typeof savedAccount.balance === 'number' &&
				typeof savedAccount.debt === 'number' &&
				typeof savedAccount.interestPaid === 'number' &&
				typeof savedAccount.nextPositionId === 'number' &&
				Array.isArray(savedAccount.positions)
			if (isValidAccount) {
				this.account = savedAccount
				this.log = Array.isArray(saved.log) ? saved.log.slice(0, 100) : []
				this.paused = saved.paused === true
				this.gameOver = saved.gameOver === true
				this.nextDayAt = saved.nextDayAt || 0
				if (saved.dayKey && saved.dayKey !== getDayKey()) {
					this.log.unshift({
						type: 'system',
						text: '新交易日开始，行情已更新',
						amount: null,
						time: this.formatTime(new Date())
					})
				}
			} else {
				this.account = createAccount(initialCents)
				this.log = []
				this.nextDayAt = 0
			}
		},
		/**
		 * 保存存档
		 */
		saveState() {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify({
					v: 1,
					dayKey: getDayKey(),
					account: this.account,
					log: this.log,
					paused: this.paused,
					gameOver: this.gameOver,
					nextDayAt: this.nextDayAt
				}))
			} catch (error) {
				// 存档失败忽略
			}
		},
		/**
		 * 缓存游戏配置到本地
		 */
		cacheConfig() {
			try {
				localStorage.setItem('fx:game:config', JSON.stringify({
					dayKey: getDayKey(),
					config: this.config
				}))
			} catch (error) {
				// 缓存失败忽略
			}
		},
		/**
		 * 加载本地缓存的游戏配置
		 *
		 * @returns {object|null} 配置对象
		 */
		loadCachedConfig() {
			try {
				const raw = localStorage.getItem('fx:game:config')
				if (!raw) {
					return null
				}
				const cached = JSON.parse(raw)
				const config = cached.config
				if (!config || !Array.isArray(config.symbols) || config.symbols.length === 0 ||
					!(config.tickIntervalMs > 0) || !Array.isArray(config.leverages)) {
					return null
				}
				return config
			} catch (error) {
				return null
			}
		}
	}
}
</script>

<style scoped>
.fx-game {
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	background:
		radial-gradient(1100px 520px at 14% -10%, rgba(77, 162, 255, 0.07), transparent 60%),
		radial-gradient(900px 460px at 86% -8%, rgba(45, 212, 191, 0.045), transparent 55%),
		linear-gradient(180deg, #070b13, #05070c 240px);
	color: var(--fx-text);
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	overflow: hidden;
	--fx-bg: #05070c;
	--fx-panel: #0b101b;
	--fx-panel-2: #0d1424;
	--fx-border: rgba(148, 170, 220, 0.1);
	--fx-border-strong: rgba(148, 170, 220, 0.22);
	--fx-text: #e8edf7;
	--fx-text-dim: rgba(205, 216, 236, 0.78);
	--fx-text-faint: rgba(205, 216, 236, 0.62);
	--fx-accent: #4da2ff;
	--fx-accent-soft: rgba(77, 162, 255, 0.16);
	--fx-up: #f5564a;
	--fx-up-soft: rgba(245, 86, 74, 0.15);
	--fx-down: #2ebd85;
	--fx-down-soft: rgba(46, 189, 133, 0.15);
	--fx-warn: #f5b13d;
	--fx-warn-soft: rgba(245, 177, 61, 0.14);
	--fx-mono: 'Cascadia Mono', 'SF Mono', Consolas, Menlo, monospace;
}

.fx-game__topbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 20px;
	flex: 0 0 auto;
	background: linear-gradient(180deg, rgba(14, 21, 36, 0.96), rgba(9, 13, 22, 0.96));
	border-bottom: 1px solid var(--fx-border);
}

.fx-game__topbar-left {
	display: flex;
	align-items: center;
	gap: 14px;
	min-width: 0;
	flex-shrink: 0;
}

.fx-game__ctrl--back {
	padding: 7px 13px;
	font-size: 13px;
	flex-shrink: 0;
}

.fx-game__ctrl--back:hover:not(:disabled) {
	border-color: rgba(77, 162, 255, 0.5);
	color: #9cc7ff;
	background: var(--fx-accent-soft);
}

.fx-game__brand {
	display: flex;
	align-items: center;
	gap: 10px;
}

.fx-game__logo {
	display: inline-flex;
	align-items: center;
	gap: 9px;
	font-size: 18px;
	font-weight: 700;
	letter-spacing: 0.04em;
	color: var(--fx-text);
}

.fx-game__logo-mark {
	display: block;
	padding: 3px;
	border-radius: 6px;
	background: rgba(77, 162, 255, 0.08);
	box-shadow: inset 0 0 0 1px rgba(148, 170, 220, 0.18);
}

.fx-game__sub {
	font-size: 12px;
	color: var(--fx-text-faint);
	letter-spacing: 0.05em;
	padding: 2px 9px;
	border: 1px solid var(--fx-border);
	border-radius: 999px;
	white-space: nowrap;
}

.fx-game__main {
	display: flex;
	align-items: stretch;
	gap: 0;
	padding: 10px 16px 0;
	flex: 0 0 auto;
	min-height: 0;
	box-sizing: border-box;
}

.fx-game__chart {
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
}

.fx-game__side {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-height: 0;
	padding-right: 2px;
	box-sizing: border-box;
	overflow-y: auto;
}

.fx-game__grip {
	position: relative;
	flex: 0 0 auto;
	z-index: 2;
	touch-action: none;
}

.fx-game__grip::after {
	content: '';
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	border-radius: 999px;
	background: rgba(148, 170, 220, 0.16);
	transition: background 0.15s, box-shadow 0.15s;
}

.fx-game__grip:hover::after,
.fx-game__grip:active::after {
	background: var(--fx-accent);
	box-shadow: 0 0 12px rgba(77, 162, 255, 0.5);
}

.fx-game__grip--v {
	width: 10px;
	cursor: col-resize;
}

.fx-game__grip--v::after {
	width: 2px;
	height: 40px;
}

.fx-game__grip--h {
	height: 10px;
	margin: 0 16px;
	cursor: row-resize;
}

.fx-game__grip--h::after {
	width: 56px;
	height: 2px;
}

.fx-game__bottom {
	display: grid;
	grid-template-columns: 1.2fr 1fr auto;
	gap: 10px;
	padding: 0 16px 10px;
	flex: 1 1 auto;
	min-height: 0;
	overflow: hidden;
}

.fx-game__positions,
.fx-game__log {
	min-height: 0;
	overflow: hidden;
}

.fx-game__controls {
	display: flex;
	flex-direction: column;
	gap: 8px;
	justify-content: flex-start;
}

.fx-game__ctrl {
	padding: 9px 16px;
	border-radius: 9px;
	border: 1px solid var(--fx-border);
	background: rgba(255, 255, 255, 0.03);
	color: var(--fx-text-dim);
	font-size: 13px;
	letter-spacing: 0.02em;
	cursor: pointer;
	white-space: nowrap;
	transition: border-color 0.18s, color 0.18s, background 0.18s, box-shadow 0.18s;
}

.fx-game__ctrl:hover:not(:disabled) {
	border-color: var(--fx-border-strong);
	color: var(--fx-text);
	background: rgba(255, 255, 255, 0.06);
}

.fx-game__ctrl:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}

.fx-game__ctrl--reset:hover:not(:disabled) {
	border-color: rgba(245, 86, 74, 0.5);
	color: #ff8d84;
	background: var(--fx-up-soft);
}

.fx-game__ctrl.is-paused {
	border-color: rgba(245, 177, 61, 0.45);
	background: var(--fx-warn-soft);
	color: #f7c269;
}

.fx-game__footer {
	padding: 8px 20px;
	flex: 0 0 auto;
	font-size: 12px;
	color: var(--fx-text-faint);
	text-align: center;
	letter-spacing: 0.03em;
	border-top: 1px solid rgba(148, 170, 220, 0.07);
}

.fx-game__toast {
	position: fixed;
	top: 20px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	border-radius: 10px;
	background: linear-gradient(180deg, rgba(232, 74, 61, 0.94), rgba(214, 60, 48, 0.94));
	color: #fff;
	font-size: 13px;
	box-shadow: 0 10px 32px rgba(220, 60, 45, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15);
	animation: fx-toast-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
	z-index: 20;
}

.fx-game__toast-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.22);
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
}

@keyframes fx-toast-in {

	from {
		opacity: 0;
		transform: translate(-50%, -8px);
	}

	to {
		opacity: 1;
		transform: translate(-50%, 0);
	}
}

.fx-game__overlay {
	position: fixed;
	inset: 0;
	background: rgba(3, 5, 10, 0.62);
	backdrop-filter: blur(6px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	animation: fx-fade-in 0.25s ease-out;
}

.fx-game__overlay-card {
	background: linear-gradient(180deg, #111829, #0a0f1b);
	border: 1px solid rgba(245, 86, 74, 0.28);
	border-radius: 16px;
	padding: 40px 60px;
	text-align: center;
	box-shadow: 0 28px 90px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.04);
	animation: fx-card-in 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.fx-game__overlay-num {
	font-family: var(--fx-mono);
	font-size: 40px;
	font-weight: 700;
	color: #ff6c60;
	text-shadow: 0 0 36px rgba(245, 86, 74, 0.45);
	margin-bottom: 6px;
}

.fx-game__overlay-title {
	font-size: 26px;
	font-weight: 700;
	color: #ff8076;
	letter-spacing: 0.06em;
	margin-bottom: 8px;
}

.fx-game__overlay-sub {
	font-size: 14px;
	color: var(--fx-text-dim);
	margin-bottom: 26px;
}

.fx-game__overlay-btn {
	padding: 11px 34px;
	border: none;
	border-radius: 10px;
	background: linear-gradient(180deg, #5cb0ff, #3f8ef2);
	color: #fff;
	font-size: 15px;
	font-weight: 600;
	letter-spacing: 0.03em;
	cursor: pointer;
	box-shadow: 0 8px 24px rgba(77, 162, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25);
	transition: transform 0.15s, box-shadow 0.2s, filter 0.15s;
}

.fx-game__overlay-btn:hover {
	transform: translateY(-1px);
	filter: brightness(1.05);
	box-shadow: 0 12px 32px rgba(77, 162, 255, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

@keyframes fx-fade-in {

	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
}

@keyframes fx-card-in {

	from {
		opacity: 0;
		transform: translateY(10px) scale(0.97);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

.fx-game__loading {
	position: fixed;
	inset: 0;
	background: #05070c;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	z-index: 30;
}

.fx-game__loading-ring {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	border: 3px solid rgba(148, 170, 220, 0.14);
	border-top-color: var(--fx-accent);
	animation: fx-spin 0.8s linear infinite;
}

.fx-game__loading-text {
	font-size: 14px;
	color: var(--fx-text-dim);
	letter-spacing: 0.08em;
}

@keyframes fx-spin {
	to {
		transform: rotate(360deg);
	}
}

.fx-game__side::-webkit-scrollbar {
	width: 8px;
}

.fx-game__side::-webkit-scrollbar-thumb {
	background: rgba(148, 170, 220, 0.16);
	border-radius: 999px;
}

.fx-game__side::-webkit-scrollbar-thumb:hover {
	background: rgba(148, 170, 220, 0.28);
}

@media (prefers-reduced-motion: reduce) {

	.fx-game__toast,
	.fx-game__overlay,
	.fx-game__overlay-card,
	.fx-game__loading-ring {
		animation: none;
	}
}

@media (max-width: 900px) {
	.fx-game {
		overflow-y: auto;
	}

	.fx-game__grip {
		display: none;
	}

	.fx-game__main {
		flex-direction: column;
		gap: 10px;
		height: auto !important;
		padding: 10px 12px 0;
	}

	.fx-game__chart {
		min-height: 320px;
	}

	.fx-game__side {
		width: 100% !important;
		overflow: visible;
	}

	.fx-game__bottom {
		grid-template-columns: 1fr;
		flex: 0 0 auto;
		height: auto;
	}

	.fx-game__positions {
		height: 200px;
	}

	.fx-game__log {
		height: 180px;
	}

	.fx-game__controls {
		flex-direction: row;
		flex-wrap: wrap;
	}
}

@media (min-width: 901px) and (max-height: 560px) {
	.fx-game {
		overflow-y: auto;
	}

	.fx-game__main {
		height: 240px !important;
	}

	.fx-game__bottom {
		flex: 0 0 auto;
		height: 190px;
	}
}
</style>
